'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CertificateForm, type CertificateFormRef } from '@/components/partials/certificates'
import { type CreateCertificateParams } from '@/types/certificate'
import { useCreateCertificate } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const certificateFormRef = useRef<CertificateFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createCertificate, isPending: isLoading } = useCreateCertificate()

  const handleSubmit = (data: CreateCertificateParams) => {
    createCertificate(data, {
      onSuccess: async newCertificate => {
        setIsRedirecting(true)
        queryClient.setQueryData(['certificate', newCertificate.id], newCertificate)
        await queryClient.invalidateQueries({ queryKey: ['certificates'] })
        toast.success(t('created_successfully'))
        router.push(`/galleries/certificates/${newCertificate.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateCertificateParams) => {
    createCertificate(data, {
      onSuccess: async newCertificate => {
        queryClient.setQueryData(['certificate', newCertificate.id], newCertificate)
        await queryClient.invalidateQueries({ queryKey: ['certificates'] })
        const detailUrl = `/galleries/certificates/${newCertificate.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        certificateFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (certificateFormRef.current) {
        certificateFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/galleries/certificates')
  }

  return (
    <CertificateForm
      key={formKey}
      ref={certificateFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
